import { createContext, useState, useCallback } from 'react';
import Modal from '../components/Modal';

const DialogContext = createContext(null);

/**
 * DialogProvider - Provides confirm() and alert() dialog functions
 * Manages dialog state and Promise resolution
 */
export function DialogProvider({ children }) {
  const [dialogState, setDialogState] = useState({
    isOpen: false,
    mode: 'confirm',
    title: '',
    message: '',
    variant: 'default',
    confirmLabel: '',
    cancelLabel: 'Cancel',
    onConfirm: null,
    onCancel: null
  });

  /**
   * Show a confirmation dialog
   * @param {Object} options - Dialog options
   * @param {string} options.title - Dialog title
   * @param {string} options.message - Dialog message
   * @param {string} [options.variant='default'] - Visual variant ('default' | 'danger')
   * @param {string} [options.confirmLabel] - Custom confirm button label
   * @param {string} [options.cancelLabel='Cancel'] - Custom cancel button label
   * @returns {Promise<boolean>} Resolves to true if confirmed, false if cancelled
   */
  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      setDialogState({
        isOpen: true,
        mode: 'confirm',
        title: options.title,
        message: options.message,
        variant: options.variant || 'default',
        confirmLabel: options.confirmLabel || '',
        cancelLabel: options.cancelLabel || 'Cancel',
        onConfirm: () => {
          setDialogState(prev => ({ ...prev, isOpen: false }));
          resolve(true);
        },
        onCancel: () => {
          setDialogState(prev => ({ ...prev, isOpen: false }));
          resolve(false);
        }
      });
    });
  }, []);

  /**
   * Show an alert dialog
   * @param {Object} options - Dialog options
   * @param {string} options.title - Dialog title
   * @param {string} options.message - Dialog message
   * @param {string} [options.variant='default'] - Visual variant ('default' | 'success' | 'error')
   * @param {string} [options.buttonLabel='OK'] - Custom button label
   * @returns {Promise<void>} Resolves when dialog is dismissed
   */
  const alert = useCallback((options) => {
    return new Promise((resolve) => {
      setDialogState({
        isOpen: true,
        mode: 'alert',
        title: options.title,
        message: options.message,
        variant: options.variant || 'default',
        confirmLabel: options.buttonLabel || 'OK',
        cancelLabel: '',
        onConfirm: () => {
          setDialogState(prev => ({ ...prev, isOpen: false }));
          resolve();
        },
        onCancel: null
      });
    });
  }, []);

  const value = {
    confirm,
    alert
  };

  return (
    <DialogContext.Provider value={value}>
      {children}
      <Modal
        isOpen={dialogState.isOpen}
        mode={dialogState.mode}
        title={dialogState.title}
        message={dialogState.message}
        variant={dialogState.variant}
        confirmLabel={dialogState.confirmLabel}
        cancelLabel={dialogState.cancelLabel}
        onConfirm={dialogState.onConfirm}
        onCancel={dialogState.onCancel}
        onClose={dialogState.onCancel}
      />
    </DialogContext.Provider>
  );
}

export { DialogContext };
